import type z from "zod/v4";
import { type CreatorSignalsFor, createConditionEngine, type FieldDefinition } from "../../utils/conditionEngine";

export type { ComparisonOperator, ConditionValue, Operator, TagOperator } from "../../utils/conditionEngine";

// ──────────────────────────────────────────────────────────────────────────
// Field registry
// ──────────────────────────────────────────────────────────────────────────

export const FIELD_DEFINITIONS = {
  gmvAmount: {
    kind: "numeric",
    label: "GMV Amount",
    tooltip: "Total GMV this creator has generated for your brand.",
  },
  followerCount: {
    kind: "numeric",
    label: "Follower Count",
    tooltip: "The creator's current follower count.",
  },
  postRate: {
    kind: "numeric",
    label: "Post Rate (All brands)",
    tooltip: "The percentage of received samples that resulted in a posted video.",
  },
  unitSales: {
    kind: "numeric",
    label: "Unit Sales",
    tooltip: "Total units sold by this creator.",
  },
  distinctProductsSold: {
    kind: "numeric",
    label: "Unique Products Sold",
    tooltip: "Number of distinct products this creator has sold.",
  },
  avgViewsPerVideo: {
    kind: "numeric",
    label: "Avg Views / Video (All Brands)",
    tooltip: "The creator's average views per video, across all brands they work with.",
  },
  gmvLast30Days: {
    kind: "numeric",
    label: "GMV (All Brands)",
    tooltip: "The creator's total GMV across all brands they work with, as of the last weekly refresh.",
  },
  cohortId: {
    kind: "tag",
    label: "Cohort",
    tooltip: "Whether the creator belongs to a specific saved cohort.",
  },
  postedVideos: {
    kind: "numeric",
    label: "Posted Videos",
    tooltip: "Number of videos this creator has posted for your brand.",
  },
  timesCollaborated: {
    kind: "numeric",
    label: "Times Collaborated",
    tooltip: "Number of distinct collaborations this creator has participated in for your brand.",
  },
} as const satisfies Record<string, FieldDefinition>;

export type FieldId = keyof typeof FIELD_DEFINITIONS;

// `except` is capped at one group — the drawer never lets a merchant build more than
// one exception group (see the frontend's `use-rule-draft.ts`: `exceptGroup = conditions.except[0] ?? null`).
const engine = createConditionEngine(FIELD_DEFINITIONS, { version: 1, maxExceptGroups: 1 });

export const ruleConditionsSchema = engine.ruleConditionsSchema;
export const matchesConditions = engine.matchesConditions;
export const referencesField = engine.referencesField;

export type Condition = z.infer<typeof engine.conditionSchema>;
export type ConditionGroup = z.infer<typeof engine.conditionGroupSchema>;
export type RuleConditions = z.infer<typeof engine.ruleConditionsSchema>;
export type CreatorSignals = CreatorSignalsFor<typeof FIELD_DEFINITIONS>;
